/* eslint radix: 0 */
import queryString from 'query-string';
import { isEmpty, cloneDeep } from 'lodash';
import { push } from 'react-router-redux';
import {
	UserService, StripeService, PlaidService, SignupService,
} from '@services';
import utils from '@utils';
import { apiErrorHandler } from '@config/utils';
import { STRIPE_KEY } from '@config';
import GlobalsActions from '@redux/globals';
import Actions from '.';

const createClient = (stringObj) => ((dispatch, getState) => {
	dispatch(Actions.changeSignupValue('loading', true));
	const { userDetails : { personalInfo }, verifiedToken } = getState().signup;
	SignupService.step1(personalInfo, verifiedToken)
		.then(({ data : { SignUpToken } }) => {
			utils.notify({
				type    : 'success',
				message : 'Client created successfully',
			});

			dispatch(push({ pathname : '/signup', search : queryString.stringify(stringObj) }));

			dispatch(Actions.updateStoreProperties({
				signUpToken   : SignUpToken,
				queryString   : '2',
				currentStep   : 1,
				loading       : false,
				verifiedToken : '',
			}));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'signup',
			property : 'loading',
		}));
});

const changeQueryParams = (stringObj) => ((dispatch) => {
	const { step } = stringObj;

	if (step === '2') { dispatch(createClient(stringObj)); } else if (step === 'done') {
		dispatch(push({ pathname : '/signup', search : queryString.stringify({ step : 'done' }) }));
		dispatch(Actions.updateStoreProperties({
			queryString : 'done',
			currentStep : 2,
			loading     : false,
		}));
	} else {
		dispatch(push({ pathname : '/signup', search : queryString.stringify(stringObj) }));
		dispatch(Actions.changeSignupValue('queryString', step || '1'));
	}
});

const handleBackAction = (currentSearchString, prevSearchString) => ((dispatch) => {
	const queryStringObj = queryString.parse(currentSearchString);
	const prevStringObj = queryString.parse(prevSearchString);
	const prevValue = isEmpty(prevStringObj) ? 1 : Math.floor(parseInt(prevStringObj.step));
	const currValue = Math.floor(parseInt(queryStringObj.step));
	if (prevValue - currValue === 1 || prevStringObj.step === 'done') {
		dispatch(push({ pathname : '/signup', search : queryString.stringify(prevStringObj) }));
		utils.notify({
			type    : 'error',
			message : 'Navigating back to previous sign up step is restricted. This action cannot be done.',
			title   : 'Oops...',
		});
	} else dispatch(Actions.changeSignupValue('queryString', isEmpty(queryStringObj) ? '1' : queryStringObj.step));
});

const verifySignupToken = (searchObj) => ((dispatch, getState) => {
	dispatch(Actions.resetSignup());
	dispatch(Actions.changeSignupValue('verificationLoading', true));
	const queryStringObj = queryString.parse(searchObj);

	if ('quickpayToken' in queryStringObj) {
		dispatch(Actions.updateStoreProperties({
			verificationLoading : false,
			verifiedToken       : queryStringObj.quickpayToken,
			invitedByUser       : 'QuickPay',
		}));
		return;
	}

	UserService.verifyClientSignupToken(queryStringObj.token).then(({ data }) => {
		const userDetails = cloneDeep(getState().signup.userDetails);
		userDetails.personalInfo.firstname = data.FirstName;
		userDetails.personalInfo.lastname = data.LastName;
		userDetails.personalInfo.email = data.EmailAddress;
		userDetails.personalInfo.phoneno = data.MobilePhone;
		userDetails.contactInfo.address = data.StreetAddress1;
		userDetails.contactInfo.city = data.City;
		userDetails.contactInfo.state = data.StateId;
		userDetails.contactInfo.zip = data.Zipcode;
		dispatch(Actions.updateStoreProperties({
			userDetails,
			verificationLoading : false,
			verifiedToken       : queryStringObj.token,
			invitedByUser       : data.InvitedByUser,
		}));
	}).catch(() => {
		// We do not want to trigger a notification, we will instead redirect the user to the login page
		dispatch(GlobalsActions.changeState('signup', 'verificationLoading', false));
	});
});

const saveBillingInfoResponseHandler = (dispatch, signUpToken, type, token) => {
	if (token.error) {
		apiErrorHandler({
			dispatch,
			module         : 'signup',
			property       : 'loading',
			defaultMessage : 'Error getting token',
		})(token.error);
		return;
	}

	SignupService.step2(signUpToken, { [type] : { TokenId : token.id } })
		.then(({ data : { Message } }) => {
			utils.notify({
				type    : 'success',
				message : Message,
			});

			dispatch(push({ pathname : '/signup', search : queryString.stringify({ step : 'done' }) }));
			dispatch(Actions.updateStoreProperties({
				queryString : 'done',
				currentStep : 2,
				loading     : false,
			}));
		})
		.catch(apiErrorHandler({
			dispatch,
			module         : 'signup',
			property       : 'loading',
			defaultMessage : 'Error saving payment method',
		}));
};

const saveBillingInfo = (formData) => (async (dispatch, getState) => {
	const { signup : { signUpToken } } = getState();

	dispatch(Actions.changeSignupValue('loading', true));

	if (formData.type === 'bankAccount') {
		const billingInfoToken = await StripeService.createBankAccountToken(STRIPE_KEY, formData.values);
		saveBillingInfoResponseHandler(dispatch, signUpToken, 'BankAccount', billingInfoToken);
	} else if (formData.type === 'creditCard') {
		saveBillingInfoResponseHandler(dispatch, signUpToken, 'PaymentCard', formData.token);
	}
});

const addBankAccountWithPlaid = (PublicToken, AccountId) => (async (dispatch, getState) => {
	const { signup : { signUpToken } } = getState();

	dispatch(Actions.changeSignupValue('loading', true));

	try {
		const { data } = await PlaidService.signUpExchangeToken({ token : signUpToken }, { PublicToken, AccountId });
		saveBillingInfoResponseHandler(dispatch, signUpToken, 'BankAccount', { id : data.BankAccountToken });
	} catch (error) {
		apiErrorHandler({
			dispatch,
			module         : 'signup',
			property       : 'loading',
			defaultMessage : 'Error getting token',
		})(error);
	}
});

const clearSignupAndRedirect = () => ((dispatch) => {
	dispatch(Actions.resetSignup());
	dispatch(push({ pathname : '/' }));
});

export default {
	changeQueryParams,
	handleBackAction,
	clearSignupAndRedirect,
	verifySignupToken,
	saveBillingInfo,
	addBankAccountWithPlaid,
};
